import { getSupabaseBrowserClient } from "@/integrations/supabase/client";

const LOCAL_STORAGE_KEY = "referral_ref";
const SESSION_STARTED_KEY = "referral_started_tracked";
const SESSION_COMPLETED_KEY = "referral_completed_tracked";

const REFERRAL_FUNCTION =
  import.meta.env.VITE_SUPABASE_OTHER_SPECIALTY_FUNCTION?.trim() ||
  "send-other-specialty";

// In-memory fallback if storage is disabled (e.g. private mode / cookies blocked)
let memoryRef: string | null = null;
let memoryStartedTracked: string | null = null;
let memoryCompletedTracked: string | null = null;

function safeSetLocalStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
    if (key === LOCAL_STORAGE_KEY) memoryRef = value;
  } catch (e) {
    console.warn("[Referral Tracker] localStorage write failed, using memory fallback:", e);
    memoryRef = value;
  }
}

function safeGetLocalStorage(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    if (key === LOCAL_STORAGE_KEY) {
      return (v && v.trim()) || memoryRef;
    }
    return v;
  } catch (e) {
    console.warn("[Referral Tracker] localStorage read failed, using memory fallback:", e);
    return key === LOCAL_STORAGE_KEY ? memoryRef : null;
  }
}

function safeRemoveLocalStorage(key: string) {
  try {
    localStorage.removeItem(key);
    if (key === LOCAL_STORAGE_KEY) memoryRef = null;
  } catch (e) {
    console.warn("[Referral Tracker] localStorage remove failed:", e);
    memoryRef = null;
  }
}

function safeSetSessionStorage(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
    if (key === SESSION_STARTED_KEY) memoryStartedTracked = value;
    if (key === SESSION_COMPLETED_KEY) memoryCompletedTracked = value;
  } catch (e) {
    console.warn("[Referral Tracker] sessionStorage write failed, using memory fallback:", e);
    if (key === SESSION_STARTED_KEY) memoryStartedTracked = value;
    if (key === SESSION_COMPLETED_KEY) memoryCompletedTracked = value;
  }
}

function safeGetSessionStorage(key: string): string | null {
  try {
    const v = sessionStorage.getItem(key);
    if (key === SESSION_STARTED_KEY) {
      return v || memoryStartedTracked;
    }
    if (key === SESSION_COMPLETED_KEY) {
      return v || memoryCompletedTracked;
    }
    return v;
  } catch (e) {
    console.warn("[Referral Tracker] sessionStorage read failed, using memory fallback:", e);
    if (key === SESSION_STARTED_KEY) return memoryStartedTracked;
    if (key === SESSION_COMPLETED_KEY) return memoryCompletedTracked;
    return null;
  }
}

/**
 * Reads `ref` from the URL (if present) and persists it for the session.
 */
export function captureReferralCode(search?: string | URLSearchParams) {
  try {
    if (typeof window === "undefined") return;
    const params =
      search instanceof URLSearchParams
        ? search
        : new URLSearchParams(search ?? window.location.search);
    const ref = params.get("ref")?.trim();
    if (ref) {
      safeSetLocalStorage(LOCAL_STORAGE_KEY, ref);
      console.info("[Referral Tracker] Captured referral code:", ref);
    }
  } catch (e) {
    console.error("[Referral Tracker] Failed to capture referral code:", e);
  }
}

export function getStoredReferralCode(): string | null {
  const ref = safeGetLocalStorage(LOCAL_STORAGE_KEY);
  return ref?.trim() || null;
}

/**
 * Clears persisted referral code and per-session tracking.
 * Call when the patient explicitly starts a fresh intake (no referral attribution).
 */
export function clearStoredReferralSession(): void {
  safeRemoveLocalStorage(LOCAL_STORAGE_KEY);
  memoryRef = null;
  try {
    sessionStorage.removeItem(SESSION_STARTED_KEY);
    sessionStorage.removeItem(SESSION_COMPLETED_KEY);
  } catch (e) {
    console.warn("[Referral Tracker] sessionStorage clear failed:", e);
  }
  memoryStartedTracked = null;
  memoryCompletedTracked = null;
}

type ReferralStatus = "intake_started" | "intake_completed";

type ReferralStatusResponse = {
  success?: boolean;
  message?: string;
};

/**
 * Updates referral status via Edge Function (service role) — required because
 * anonymous clients cannot update the referrals table directly (RLS).
 */
async function invokeReferralStatusUpdate(
  ref: string,
  status: ReferralStatus,
  patientName?: string,
): Promise<boolean> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) {
    console.warn("[Referral Tracker] Supabase client is not available.");
    return false;
  }

  console.info(
    `[Referral Tracker] Invoking '${REFERRAL_FUNCTION}' → status '${status}' for:`,
    ref,
  );

  const { data, error } = await supabase.functions.invoke(REFERRAL_FUNCTION, {
    body: {
      type: "referral_status_update",
      uniqueCode: ref,
      status,
      ...(patientName ? { patientName } : {}),
    },
  });

  if (error) {
    console.error(
      `[Referral Tracker] Edge function error (${status}):`,
      error,
    );
    return false;
  }

  const payload = data as ReferralStatusResponse | null;
  if (payload?.success !== true) {
    console.error(
      `[Referral Tracker] Edge function returned failure (${status}):`,
      payload?.message ?? data,
    );
    return false;
  }

  console.info(`[Referral Tracker] Successfully set status to '${status}'`);
  return true;
}

/**
 * When intake opens with a referral link, mark the referral as intake_started.
 */
export async function trackReferralStarted() {
  try {
    captureReferralCode();
    const ref = getStoredReferralCode();
    if (!ref) return;

    const alreadyTracked = safeGetSessionStorage(SESSION_STARTED_KEY);
    if (alreadyTracked === ref) return;

    const ok = await invokeReferralStatusUpdate(ref, "intake_started");
    if (ok) {
      safeSetSessionStorage(SESSION_STARTED_KEY, ref);
    }
  } catch (e) {
    console.error("[Referral Tracker] Error in trackReferralStarted:", e);
  }
}

/**
 * When intake is fully finished, mark intake_completed and notify referrer.
 * Safe to call more than once — deduped per session per referral code.
 */
export async function trackReferralCompleted(patientName: string) {
  try {
    const ref = getStoredReferralCode();
    if (!ref) return;

    const alreadyCompleted = safeGetSessionStorage(SESSION_COMPLETED_KEY);
    if (alreadyCompleted === ref) return;

    const ok = await invokeReferralStatusUpdate(
      ref,
      "intake_completed",
      patientName,
    );
    if (ok) {
      safeSetSessionStorage(SESSION_COMPLETED_KEY, ref);
      safeRemoveLocalStorage(LOCAL_STORAGE_KEY);
    }
  } catch (e) {
    console.error("[Referral Tracker] Error in trackReferralCompleted:", e);
  }
}
